import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { onb, showValue, factValue } from "@/lib/onb";
import { errText } from "@/lib/http";
import { Panel, Eyebrow, timeAgo } from "@/components/mh/bits";

const Fact = ({ section, k, f, canEdit, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(showValue(factValue(f)));
  const save = async () => {
    try { await onb.editFact(section, k, v.includes(" · ") ? v.split(" · ").map((x) => x.trim()).filter(Boolean) : v); setEdit(false); onSaved(); toast.success("Saved. Re-tailor to apply it"); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-0" data-testid={`fact-${section}-${k}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {k.replace(/_/g, " ")}
        <span className="normal-case tracking-normal text-[#00E5FF]/80" data-testid={`fact-source-${section}-${k}`}>· {f.source}{f.by ? ` · ${f.by}` : ""}{f.at ? ` · ${timeAgo(f.at)}` : ""}</span>
        {canEdit && !edit && <button onClick={() => setEdit(true)} className="ml-auto hover:text-foreground" aria-label="Edit" data-testid={`fact-edit-${section}-${k}`}><Pencil className="w-3 h-3" /></button>}
      </div>
      {edit ? (
        <div className="mt-1.5 flex gap-2">
          <textarea value={v} onChange={(e) => setV(e.target.value)} rows={2} data-testid={`fact-input-${section}-${k}`}
            className="flex-1 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#D4FF3A]/60" />
          <div className="flex flex-col gap-1">
            <button onClick={save} className="h-8 px-3 rounded-md bg-[#D4FF3A] text-black text-xs font-semibold" data-testid={`fact-save-${section}-${k}`}>Save</button>
            <button onClick={() => setEdit(false)} className="h-8 px-3 rounded-md border border-white/10 text-xs">Cancel</button>
          </div>
        </div>
      ) : <div className="text-sm mt-0.5 whitespace-pre-wrap">{showValue(factValue(f))}</div>}
    </div>
  );
};

const Section = ({ title, section, facts, canEdit, onSaved, hex }) => (
  <Panel cls="p-5" testId={`profile-section-${section}`}>
    <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rotate-45" style={{ background: hex || "#D4FF3A" }} /><Eyebrow>{title}</Eyebrow></div>
    {Object.keys(facts || {}).length === 0 ? <p className="text-sm text-muted-foreground mt-2">Nothing yet. This fills in as the interview runs.</p>
      : <div className="mt-1">{Object.entries(facts).map(([k, f]) => <Fact key={k} section={section} k={k} f={f} canEdit={canEdit} onSaved={onSaved} />)}</div>}
  </Panel>
);

export const Profile = ({ st, reload, canEdit }) => {
  const p = st.profile;
  return (
    <div className="space-y-4" data-testid="onb-profile">
      <p className="text-sm text-muted-foreground max-w-2xl">Your Business Profile. Every fact shows where it came from. Edit anything, then re-tailor so the map picks it up.</p>
      <Section title="Company" section="company" facts={p.company} canEdit={canEdit} onSaved={reload} />
      <div className="grid md:grid-cols-2 gap-4">
        {st.interviews.departments.map((d) => <Section key={d.slug} title={d.name} section={d.slug} hex={d.hex} facts={(p.departments || {})[d.slug]} canEdit={canEdit} onSaved={reload} />)}
      </div>
    </div>
  );
};
